/**
 * /start/products — funnel step 3 (Product Selection, spec §3.7).
 *
 * All active offerings (GET /catalog/offerings) as selectable cards —
 * preselected from the saved intent / ?plan= deep link — plus compatible
 * add-ons (GET /catalog/addons?offeringId=) as checkboxes. The orb add-on
 * is hidden when the selected plan already includes it. The selection is
 * handed to /start/payment via sessionStorage; prices shown here are
 * display-only (checkout recomputes the total server-side).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FunnelLayout } from '@/components/funnel/FunnelLayout';
import { useAddons, useFunnelState, useOfferings } from '@/hooks/useFunnel';
import {
  formatPrice,
  readFunnelSelection,
  readPreferredPlanSlug,
  storeFunnelSelection,
} from '@/lib/funnel';
import { cn } from '@/lib/utils';
import type { AddonProduct, ProductOffering } from '@/types';

// Static add-on thumbnails keyed by catalog slug (ClickUp 86banyvpy) — UI-only
// until the catalog grows an imageUrl field. Unmapped slugs render no image.
const ADDON_IMAGES: Record<string, string> = {
  'physical-orb': '/images/addons/legacy-orb.png',
};

export function ProductSelectionPage() {
  const navigate = useNavigate();
  const { data: funnelState } = useFunnelState();
  const { data: offerings, isLoading, isError, refetch } = useOfferings();

  const [selectedOfferingId, setSelectedOfferingId] = useState<string | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);

  const selectedOffering = offerings?.find((o) => o.id === selectedOfferingId) ?? null;

  const {
    data: addons,
    isLoading: addonsLoading,
    isError: addonsError,
    refetch: refetchAddons,
  } = useAddons(selectedOfferingId ?? undefined);

  // Hide the orb add-on for plans that already include it (spec §3.7); the
  // API also rejects double-selling it at checkout.
  const visibleAddons = (addons ?? []).filter(
    (addon) => !(selectedOffering?.includesOrb && addon.slug === 'physical-orb')
  );

  // Preselect: a selection saved on a previous visit (back from payment) >
  // the saved intent's preferred package > the ?plan= deep link.
  useEffect(() => {
    if (!offerings || selectedOfferingId) return;

    // Never preselect a coming-soon plan (a saved selection or deep link may
    // predate the status flip) — the server rejects it at checkout anyway.
    const saved = readFunnelSelection();
    const savedOffering = saved ? offerings.find((o) => o.id === saved.offeringId) : undefined;
    if (saved && savedOffering && savedOffering.status !== 'coming_soon') {
      setSelectedOfferingId(saved.offeringId);
      setSelectedAddonIds(saved.addonIds);
      return;
    }

    const slug = funnelState?.intent?.preferredOfferingSlug ?? readPreferredPlanSlug();
    const preferred = slug ? offerings.find((o) => o.slug === slug) : undefined;
    if (preferred && preferred.status !== 'coming_soon') {
      setSelectedOfferingId(preferred.id);
    }
  }, [offerings, funnelState, selectedOfferingId]);

  const handleSelectOffering = (offering: ProductOffering) => {
    if (offering.status === 'coming_soon') return;
    setSelectedOfferingId(offering.id);
    // Compatibility differs per plan — re-validated against the newly
    // fetched add-on list before continuing (see handleContinue).
    setSelectedAddonIds([]);
  };

  const toggleAddon = (addonId: string) => {
    setSelectedAddonIds((ids) =>
      ids.includes(addonId) ? ids.filter((id) => id !== addonId) : [...ids, addonId]
    );
  };

  // Only count add-ons that are actually visible/compatible for the summary
  // (and never coming-soon ones — a stale saved selection may reference one).
  const chosenAddons = visibleAddons.filter(
    (a) => selectedAddonIds.includes(a.id) && a.status !== 'coming_soon'
  );
  const totalCents = selectedOffering
    ? selectedOffering.priceCents + chosenAddons.reduce((sum, a) => sum + a.priceCents, 0)
    : 0;

  const handleContinue = () => {
    if (!selectedOffering || selectedOffering.status === 'coming_soon') return;
    storeFunnelSelection({
      offeringId: selectedOffering.id,
      addonIds: chosenAddons.map((a) => a.id),
    });
    navigate('/start/shipping');
  };

  return (
    <FunnelLayout step={3}>
      <div className="mb-6 text-center">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          Choose your package
        </h1>
        <p className="mt-2 text-muted-foreground">
          Select the plan that fits your legacy — you can add extras below.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : isError || !offerings ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-sm text-destructive">Failed to load the product catalog.</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {offerings.map((offering) => (
              <OfferingCard
                key={offering.id}
                offering={offering}
                isSelected={offering.id === selectedOfferingId}
                onSelect={() => handleSelectOffering(offering)}
              />
            ))}
          </div>

          {selectedOffering && (
            <Card>
              <CardContent className="pt-6">
                <h2 className="mb-1 text-base font-semibold text-foreground">Add-ons</h2>
                <p className="mb-4 text-sm text-muted-foreground">
                  Optional extras available with {selectedOffering.name}.
                </p>

                {selectedOffering.includesOrb && (
                  <p className="mb-4 rounded-md border border-border bg-muted/50 p-3 text-sm text-foreground">
                    The Physical Legacy Orb is already included in this plan.
                  </p>
                )}

                {addonsLoading ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : addonsError ? (
                  // Distinguish a failed fetch from a genuinely empty list
                  // (review F8) — "no add-ons available" here would mislead.
                  <div className="flex flex-col items-center gap-3 py-4 text-center">
                    <p className="text-sm text-destructive">
                      We could not load the add-ons for this plan.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => refetchAddons()}>
                      Try Again
                    </Button>
                  </div>
                ) : visibleAddons.length === 0 ? (
                  !selectedOffering.includesOrb && (
                    <p className="text-sm text-muted-foreground">
                      No add-ons are available for this plan.
                    </p>
                  )
                ) : (
                  <div className="space-y-3">
                    {visibleAddons.map((addon) => (
                      <AddonRow
                        key={addon.id}
                        addon={addon}
                        checked={selectedAddonIds.includes(addon.id)}
                        onToggle={() => toggleAddon(addon.id)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {selectedOffering && (
            <Card>
              <CardContent className="pt-6">
                <h2 className="mb-4 text-base font-semibold text-foreground">Order summary</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground">
                      {selectedOffering.name}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {selectedOffering.planLabel}
                      </span>
                    </span>
                    <span className="text-foreground">
                      {formatPrice(selectedOffering.priceCents)}
                    </span>
                  </div>
                  {chosenAddons.map((addon) => (
                    <div key={addon.id} className="flex items-center justify-between">
                      <span className="text-muted-foreground">{addon.name}</span>
                      <span className="text-muted-foreground">{formatPrice(addon.priceCents)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-t border-border pt-3 text-base font-semibold text-foreground">
                    <span>Total</span>
                    <span>{formatPrice(totalCents)}</span>
                  </div>
                  {selectedOffering.billingType === 'first_year_then_recurring' &&
                    selectedOffering.recurringPriceCents != null && (
                      <p className="text-xs text-muted-foreground">
                        First year, then {formatPrice(selectedOffering.recurringPriceCents)}/
                        {selectedOffering.recurringInterval ?? 'month'} thereafter.
                      </p>
                    )}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            <Button variant="outline" size="lg" onClick={() => navigate('/start/agreement')}>
              Back to Agreement
            </Button>
            <Button
              size="lg"
              onClick={handleContinue}
              disabled={!selectedOffering || selectedOffering.status === 'coming_soon'}
            >
              Continue to Shipping
            </Button>
          </div>
        </div>
      )}
    </FunnelLayout>
  );
}

function OfferingCard({
  offering,
  isSelected,
  onSelect,
}: {
  offering: ProductOffering;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const comingSoon = offering.status === 'coming_soon';
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={comingSoon}
      aria-pressed={isSelected}
      className={cn(
        'relative flex h-full flex-col rounded-lg border bg-card p-5 text-left transition-colors',
        comingSoon
          ? 'cursor-not-allowed border-border opacity-60'
          : isSelected
            ? 'border-primary ring-1 ring-primary'
            : 'border-border hover:border-foreground/30'
      )}
    >
      {/* No stale check-mark on a plan that flipped to coming-soon after
          being selected (a refocus refetch can update the card in place). */}
      {isSelected && !comingSoon && (
        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-4 w-4" />
        </span>
      )}

      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {offering.planLabel}
      </p>
      <div className="mt-1 flex items-center gap-2">
        <h3 className="font-display text-lg font-bold text-foreground">{offering.name}</h3>
        {comingSoon ? (
          <Badge variant="outline">Coming Soon</Badge>
        ) : (
          offering.badge && <Badge variant="secondary">{offering.badge}</Badge>
        )}
      </div>
      {offering.tagline && <p className="mt-1 text-sm text-muted-foreground">{offering.tagline}</p>}

      <p className="mt-3 text-2xl font-bold text-foreground">
        {formatPrice(offering.priceCents)}
        {offering.billingType === 'first_year_then_recurring' &&
          offering.recurringPriceCents != null && (
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              first year, then {formatPrice(offering.recurringPriceCents)}/
              {offering.recurringInterval ?? 'month'}
            </span>
          )}
      </p>
      {offering.collectionPeriodMonths != null ? (
        <p className="text-xs text-muted-foreground">
          {offering.collectionPeriodMonths}-month collection period
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">Ongoing collection</p>
      )}

      <ul className="mt-4 space-y-1.5">
        {offering.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            {feature}
          </li>
        ))}
      </ul>
    </button>
  );
}

function AddonRow({
  addon,
  checked,
  onToggle,
}: {
  addon: AddonProduct;
  checked: boolean;
  onToggle: () => void;
}) {
  const comingSoon = addon.status === 'coming_soon';
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-md border p-4 transition-colors',
        comingSoon
          ? 'cursor-not-allowed border-border opacity-60'
          : checked
            ? 'cursor-pointer border-primary'
            : 'cursor-pointer border-border hover:bg-muted/50'
      )}
    >
      <input
        type="checkbox"
        checked={checked && !comingSoon}
        disabled={comingSoon}
        onChange={onToggle}
        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
      />
      {ADDON_IMAGES[addon.slug] && (
        <img
          src={ADDON_IMAGES[addon.slug]}
          alt=""
          className="h-14 w-14 shrink-0 rounded-md border border-border bg-muted/30 object-contain"
        />
      )}
      <span className="flex-1">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground">
          {addon.name}
          {comingSoon && <Badge variant="outline">Coming Soon</Badge>}
        </span>
        {addon.description && (
          <span className="mt-0.5 block text-sm text-muted-foreground">{addon.description}</span>
        )}
      </span>
      <span className="text-sm font-medium text-foreground">{formatPrice(addon.priceCents)}</span>
    </label>
  );
}
